import { html, nothing } from "lit";
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import {
  getBraintreeClientToken,
  getBraintreeClientTokenApiStatus,
  isPurchaseOrderSelected,
  updateCountryCodeCurrencyCode,
} from '../../checkout/shop-checkout';
import { BusinessUnit, ClientTokenRequest } from "../../checkout/shop-checkout.interface";
import { getCart, getCartStatus, getProductsFromCart, isCartValid } from "../../cart/shop-cart/store/shop-cart.selectors";
import { ShopCart } from "../../cart/shop-cart/interfaces/shop-cart.interface";
import store, { waitForCart, waitForUser } from "../../../store/store";
import { StoreState } from "../../../store/store.interface";
import { isLoadingPaypalDependencies } from "./utils/paypal.util";
import { loading } from "../../../common/constants/api-constants"
import { BaseComponent } from "../../../features/base-component/base-component";
import { isFailureUser, isSuccessUser } from "../../user-profile/user-profile.selectors";
import { ActionStatus } from "../../../common/constants/action-status";
import { getLoadPaypalSdkStatus, loadPaypalSdk } from "../braintree";
import { waitForElementsToLoad } from "../../../common/utils/delay-util";
import emit from "../../../common/events";
import { getShopCart } from "../../cart/shop-cart/thunks/get-shop-cart.thunk";
import { hasOnlyFreeTrialProducts } from "../../../common/utils/cart-util";

/**
 * Component to show Paypal messages. 
 * This component is rendered without shadow DOM and needs to be in the main DOM of the page to work.
 */
@customElement(`mhecomm-paypal-messages`)
export class PaypalMessages extends connect(store)(BaseComponent) {

  /**
   * Sets the font size of the text for text layout messages.
   */
  @property({type: Number})
  size: 10 | 11 | 12 | 13 | 14 | 15 | 16 = 14

  /**
   * Informs PayPal's analytics and system logging of the type of e-commerce page on which the message is placed.
   */
  @property()
  placement: "home"| "category" | "cart" | "product" | "payment" = "cart";
  /**
   * Currency for Paypal transaction.
   */
  @state()
  private currencyCode = 'USD';

  /**
   * Country code of the order. Default US.
   */
  @state()
  private countryCode = 'US';

  /**
   * Total amount of the order.
   */
  @state()
  private totalAmount = 0;

  /**
   * Braintree Client token to initialize Paypal message.
   */
  @state()
  private clientToken = '';

  /**
   * Flag to indicate component is loading
   */
  @state()
  private isLoading = true;

  @state()
  private showPayPalMessage = false;

  private isCartValid = false;

  private shippable?: boolean;

  /**
   * Remove shadow DOM
   * @returns 
   */
  createRenderRoot() {
    return this;
  }

  renderComp() {
    return html `${this.isLoading ? html`${loading()}` 
      : html`${this.showPayPalMessage ? html`<div id="paypal-message"></div>` : nothing }`
    }`; 
  }

  /**
   * If user is logged out, do not show paypal Message.
   * When Braintree client token response is successful, update client token, paypalEnabled and showPayPalMessage flag.
   * Whenever cart is updated, check if cart is valid. 
   * If cart is valid, update the paypal Message with latest amount and update showPayPalMessage flag.
   * @param state 
   */
  stateChanged(state: StoreState) {
    this.isLoading = isLoadingPaypalDependencies();
    waitForUser().then(() => {
      if (isFailureUser(state)) {
        this.isLoading = false;
        this.showPayPalMessage = false;
      } else if (isSuccessUser(state)) {
        if (getBraintreeClientTokenApiStatus(state) === ActionStatus.failed) {
          this.showPayPalMessage = false;
          return;
        }
        if(getCartStatus(state) === ActionStatus.succeeded ){
          const cart: ShopCart = getCart(state)!;
          this.isCartValid = isCartValid(state);
          if (this.isCartValid) {
            this.totalAmount = cart.totalAmount;
            this.shippable = cart.shippable;
          }
          
          this.clientToken = state.shopCheckoutState.clientToken.data?.token || "";
          const showPaypalMessage = this.shouldShowPaypalMessage(state);
          if (!this.showPayPalMessage && showPaypalMessage) {
            this.setupPaypalMessage();
          }
          this.showPayPalMessage = showPaypalMessage && !isPurchaseOrderSelected(state);
          this.checkBraintreeClientToken();
        }
        if (getCartStatus(state) === ActionStatus.failed) {
          this.showPayPalMessage = false;
          return;
        }
        if(getLoadPaypalSdkStatus(state) === ActionStatus.succeeded && this.showPayPalMessage){
          waitForElementsToLoad(this, [
            "#paypal-message",
          ]).then(() =>
          window.paypal.Messages({
            amount: this.totalAmount,
            placement: this.placement,
            style: {
              layout: "text",
              text: {
                size: this.size
              }
            },
            onClick: () => {
              emit({
                element: this,
                name: 'click-paypal-message',
                detail: {
                  comp: "text",
                  label: "PayPal Learn More",
                  url: window.location.href,
                }
              });
            },
            onApply: () => {
              emit({
                element: this,
                name: 'click-apply-in-paypal-message-modal',
                detail: {
                  comp: "text",
                  label: "PayPal Credit Apply",
                  url: window.location.href,
                }
              });
            }
          }).render("#paypal-message"));
        }
      }
    });
  }

  shouldShowPaypalMessage(state: StoreState) {
    const hasOnlyFreeTrial = hasOnlyFreeTrialProducts(getProductsFromCart(state));
    const isValidCartForPaypal = !!state.shopCheckoutState.clientToken.data?.paypalEnabled && this.isCartValid;
    return isValidCartForPaypal && !hasOnlyFreeTrial;
  }

  checkBraintreeClientToken() {
    if (!this.isLoading && this.isCartValid && this.clientToken === "") {
      this.dispatchBraintreeClientToken();
    }
  }

  /** 
   * When Paypal message loads on cart page, check if user is logged-in. 
   * Set country and currency codes. 
   * Validate cart.
   * If cart is valid get Braintree client token.
   * @param _changedProperties 
   */
  async firstUpdated(
    _changedProperties: Map<string | number | symbol, unknown>
  ) {
    super.firstUpdated(_changedProperties);
    store.dispatch(
      updateCountryCodeCurrencyCode({
        currencyCode: this.currencyCode,
        countryCode: this.countryCode,
      })
    );
    store.dispatch(getShopCart({}));
    await waitForUser();
    await waitForCart();
    if (isSuccessUser(store.getState() as StoreState) && isCartValid(store.getState() as StoreState)) {
      this.dispatchBraintreeClientToken();
    }
  }

  dispatchBraintreeClientToken() {
    const cart = getCart(store.getState() as StoreState);
    const clientTokenRequest: ClientTokenRequest = {
      type: 'cart',
      countryCode: "US",
      currencyCode: "USD",
      businessUnit: cart?.orderBusinessUnit as BusinessUnit
    };
    store.dispatch(getBraintreeClientToken(clientTokenRequest));
  }

  async setupPaypalMessage(){
    store.dispatch(loadPaypalSdk({
      clientToken: this.clientToken,
      userAutoLogin: true,
      'enable-funding': 'paylater',
      currencyCode: this.currencyCode,
      intent: this.shippable ? 'authorize' : 'capture'
    }));
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-paypal-messages': PaypalMessages;
  }
  interface Window {
      paypal: any;
  }
}
